import $ from 'jquery';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';
import registerJQueryPlugin from 'styleguide/lib/register-jquery-plugin';

export const PLUGIN_NAME = 'stickyBound';
const DATA_ACTIVE = 'stickyBoundActive';

/**
 * @typedef {Object} StickyBoundOptions
 * @property {string}  fixedClass        - CSS class applied when element is fixed
 * @property {string|null} containerSelector - Selector for the bounding container; defaults to direct parent
 * @property {boolean} centerInContainer - Horizontally center the fixed element within its container
 * @property {number}  throttleWait      - ms to throttle scroll/resize handlers (0 disables throttle)
 */

/** @type {StickyBoundOptions} */
export const DEFAULTS = {
  fixedClass: 'sticky-bound--is-fixed',
  containerSelector: null,
  centerInContainer: false,
  throttleWait: 100,
};

/**
 * Inserts a zero-height invisible element directly before $el.
 * Stays in normal document flow at all times, giving a stable reference
 * for the element's natural document position even when $el is fixed.
 * @param {jQuery} $el
 * @returns {jQuery} $positionHolder
 */
const createPositonHolder = ($el) => {
  const $positionHolder = $('<div>', {
    'aria-hidden': 'true',
    css: { height: 0, visibility: 'hidden', 'pointer-events': 'none' },
  });
  $el.before($positionHolder);
  return $positionHolder;
};

/**
 * @param {jQuery} $el
 * @param {string|null} containerSelector
 * @returns {jQuery}
 */
const resolveContainer = ($el, containerSelector) =>
  containerSelector ? $el.closest(containerSelector) : $el.parent();

/**
 * Determines fixed/unfixed state and applies the fixedClass and centering styles.
 * Called on every scroll tick and after resize re-measurement.
 * @param {jQuery} $el
 * @param {jQuery} $positionHolder
 * @param {jQuery} $container
 * @param {{elFixedTop: number}} cache
 * @param {StickyBoundOptions} opts
 * @param {number} scrollTop - current window scroll position
 */
const evaluate = ($el, $positionHolder, $container, cache, opts, scrollTop) => {
  const elementNaturalTop = $positionHolder.offset().top;
  const { elFixedTop } = cache;
  const { fixedClass, centerInContainer } = opts;

  // fixed while positionHolder hasn't reached the element's fixed top position
  const shouldFix = (elementNaturalTop - scrollTop) > elFixedTop;

  $el.toggleClass(fixedClass, shouldFix);

  if (centerInContainer) {
    if (shouldFix) {
      const rect = $container[0].getBoundingClientRect();
      $el.css({ left: rect.left + rect.width / 2, transform: 'translateX(-50%)' });
    } else {
      $el.css({ left: '', transform: '' });
    }
  }
};

/**
 * Returns true if the entire element fits within the initial viewport,
 * meaning it is visible without scrolling and does not need to be fixed.
 * @param {jQuery} $positionHolder
 * @param {jQuery} $el
 * @returns {boolean}
 */
const isNaturallyInView = ($positionHolder, $el) =>
  $positionHolder.offset().top + $el.outerHeight() <= window.innerHeight;

/**
 * Binds scroll and resize listeners. Manages active/inactive state via
 * DATA_ACTIVE so scroll handlers are only live when the plugin needs them.
 * @param {jQuery} $el
 * @param {jQuery} $positionHolder
 * @param {jQuery} $container
 * @param {{elFixedTop: number}} cache
 * @param {StickyBoundOptions} opts
 * @param {jQuery} $window
 */
const setEventListeners = ($el, $positionHolder, $container, cache, opts, $window) => {
  const { throttleWait, fixedClass } = opts;
  const maybeThrottle = (fn) => (throttleWait > 0 ? throttle(fn, throttleWait) : fn);

  const onScroll = () => {
    if (!$el.data(DATA_ACTIVE)) return;
    evaluate($el, $positionHolder, $container, cache, opts, $window.scrollTop());
  };

  const bindScroll = () => {
    $window.on(`scroll.${PLUGIN_NAME}`, maybeThrottle(onScroll));
    $window.on(`scroll.${PLUGIN_NAME}--settle`, debounce(onScroll, 150));
  };

  /** Measures elFixedTop, binds scroll listeners, and runs initial evaluate. */
  const activate = () => {
    $el.data(DATA_ACTIVE, true);
    $el.addClass(fixedClass);
    cache.elFixedTop = $el[0].getBoundingClientRect().top;
    bindScroll();
    evaluate($el, $positionHolder, $container, cache, opts, $window.scrollTop());
  };

  /** Releases fixed state and removes scroll listeners. */
  const deactivate = () => {
    $el.data(DATA_ACTIVE, false)
      .removeClass(fixedClass)
      .css({ left: '', transform: '' });
    $window.off(`scroll.${PLUGIN_NAME}`).off(`scroll.${PLUGIN_NAME}--settle`);
  };

  const onResize = () => {
    const inView = isNaturallyInView($positionHolder, $el);
    const isActive = $el.data(DATA_ACTIVE);

    if (inView) {
      if (isActive) deactivate();
      return;
    }

    if (!isActive) {
      activate();
      return;
    }

    // Still active — re-measure elFixedTop for new viewport size
    $el.addClass(fixedClass);
    cache.elFixedTop = $el[0].getBoundingClientRect().top;
    evaluate($el, $positionHolder, $container, cache, opts, $window.scrollTop());
  };

  if (!isNaturallyInView($positionHolder, $el)) activate();

  $window.on(`resize.${PLUGIN_NAME}`, maybeThrottle(onResize));
};

/**
 * jQuery plugin that keeps an element `position: fixed` until its natural
 * document position (tracked by a zero-height positionHolder sibling) scrolls
 * back into the viewport, then releases it to normal flow with no visual jump.
 *
 * Skips activation if the element fits within the initial viewport. Re-activates
 * on resize if the viewport shrinks and the element falls below the fold.
 *
 * @param {HTMLElement} el
 * @param {Partial<StickyBoundOptions>} options
 */
export default function stickyBound(el, options = {}) {
  const $el = $(el);
  const opts = Object.assign({}, DEFAULTS, $el.data(), options);
  const $window = $(window);

  const $container = resolveContainer($el, opts.containerSelector);
  const $positionHolder = createPositonHolder($el);
  const cache = {};

  $el.data(DATA_ACTIVE, false);
  setEventListeners($el, $positionHolder, $container, cache, opts, $window);
}

registerJQueryPlugin(PLUGIN_NAME, stickyBound);



// WEBPACK FOOTER //
// ./src/scripts/entry-points/pdp/sticky-bound.js